import { paths } from '../routes';
import { slotFitsVenueForm } from '../pages/rentals/CreateBooking/slotPrefill';

/**
 * Can the create page's form take a slot `[from, to)` (ISO instants) as it
 * stands? `timeZone` is the zone of the slot's location (#3219) — the clock
 * the form reads the slot on.
 */
export type SlotFits = (from: string, to: string, timeZone?: string) => boolean;

export interface BookingServiceConfig {
    id: string;
    translationKey: string;
    route: string;
    requiresPermission?: string;
    /**
     * Whether an admin-ui "create booking" page is actually implemented and
     * routed for this service. When `false`, the create option is hidden so we
     * don't navigate to an unregistered route (which renders a blank screen —
     * the layout Outlet matches no child and there is no `path="*"` fallback).
     * Defaults to implemented when omitted.
     */
    createImplemented?: boolean;
    /**
     * Set when the create page accepts an object and a time slot from the URL
     * (`pages/rentals/CreateBooking/slotPrefill.ts`), so a calendar can open
     * it on a free slot (#3039). A capability of the page, not of the use
     * case: a page gains it by reading the prefill, and every calendar that
     * offers "book this gap" asks this rather than a use-case list.
     *
     * `fits` is the page's own answer to "can my form take this slot?" — its
     * hours, and for a page that books nights its minimum stay — so a
     * calendar never opens a form on a value the form cannot show.
     */
    slotPrefill?: { fits: SlotFits };
}

/**
 * Central mapping from service type to booking configuration
 * This is the SINGLE SOURCE OF TRUTH for all booking-related routing and translations
 *
 * When adding a new service:
 * 1. Add the service type to TenantConfig services array
 * 2. Add the mapping here
 * 3. Add translation keys to i18n files
 * 4. Create the corresponding route in routes.ts
 */
export const BOOKING_SERVICE_MAP: Record<string, BookingServiceConfig> = {
    apartment: {
        id: 'apartment',
        translationKey: 'bookings.create.guest_apartments.title',
        route: paths.createRoomBooking,
    },
    venue: {
        id: 'venue-reservation',
        translationKey: 'bookings.create.venues.title',
        route: paths.createVenueBooking,
        slotPrefill: { fits: slotFitsVenueForm },
    },
    // A sports hall books on the venue screen (PO, 2026-10-18), with its own
    // route so the screen knows its use case.
    hall: {
        id: 'hall',
        translationKey: 'bookings.create.halls.title',
        route: paths.createHallBooking,
        slotPrefill: { fits: slotFitsVenueForm },
    },

    // rental / storage / sup share the generalized CreateNonRoomBookingPage
    // (one flow parameterized by use case — Step 2 of the product-rental/
    // storage/SUP self-service plan).
    rental: {
        id: 'rental',
        translationKey: 'bookings.create.rental.title',
        route: paths.createRentalBooking,
    },

    storage: {
        id: 'storage',
        translationKey: 'bookings.create.storage.title',
        route: paths.createStorageBooking,
    },

    sup: {
        id: 'sup',
        translationKey: 'bookings.create.sup.title',
        route: paths.createSupBooking,
    },
    keys: {
        id: 'keys',
        translationKey: 'bookings.create.keys.title',
        route: paths.createKeysBooking,
        createImplemented: false,
    },
    parcel: {
        id: 'parcel',
        translationKey: 'bookings.create.parcel.title',
        route: paths.createParcelBooking,
        createImplemented: false,
    },
};

/**
 * Helper function to get booking options for a tenant
 * @param services - Array of services from tenant config
 * @param createBookingOptions - Optional array of specific services to show in dropdown
 * @returns Array of BookingServiceConfig objects
 */
export const getCreateBookingOptionsForTenant = (services: string[], createBookingOptions: string[]): BookingServiceConfig[] => {
    // Fall back to the tenant's services when no explicit override is given.
    // An empty array must fall through too: `[] || services` returns `[]`
    // (an empty array is truthy in JS), which previously left tenants
    // configured with `createBookingOptions: []` (e.g. badesee-petershagen,
    // strandbad-pertisau) with a disabled "create" button.
    const servicesToShow = createBookingOptions?.length ? createBookingOptions : services;

    return servicesToShow
        .filter((service) => BOOKING_SERVICE_MAP[service]) // Only services with booking config
        // Hide services whose create-booking page isn't implemented/routed yet
        // (navigating there would render a blank screen).
        .filter((service) => BOOKING_SERVICE_MAP[service].createImplemented !== false)
        .map((service) => BOOKING_SERVICE_MAP[service]);
};

/** Where a free slot is booked: the create page, and what its form can take. */
export interface SlotBookingTarget {
    route: string;
    fits: SlotFits;
}

/**
 * The create page a free slot of an object with these use cases opens, or
 * `null` when none of the tenant's create pages accepts a slot for it.
 *
 * Goes through `getCreateBookingOptionsForTenant`, so a calendar never offers
 * a create page the tenant's own "Create booking" menu would not.
 */
export const getSlotBookingTarget = (
    objectUseCases: string[] | undefined,
    services: string[],
    createBookingOptions: string[],
): SlotBookingTarget | null => {
    const offered = new Set(
        getCreateBookingOptionsForTenant(services, createBookingOptions).map((option) => option.route),
    );
    for (const useCase of objectUseCases ?? []) {
        const config = BOOKING_SERVICE_MAP[useCase.toLowerCase()];
        if (config?.slotPrefill && offered.has(config.route)) {
            return { route: config.route, fits: config.slotPrefill.fits };
        }
    }
    return null;
};
